import {
  Component, Input, Output, EventEmitter,
  forwardRef, signal, computed, input,
  ChangeDetectionStrategy, ViewEncapsulation,
  ElementRef, inject, OnDestroy, afterNextRender,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { CheckboxComponent } from '../checkbox/checkbox.component';

export interface SelectOption {
  id:        any;
  label:     string;
  disabled?: boolean;
}

export type SelectSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

@Component({
  selector:        'app-select',
  standalone:      true,
  imports:         [CommonModule, FormsModule, ReactiveFormsModule, CheckboxComponent],
  templateUrl:     './select.component.html',
  styleUrls:       ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation:   ViewEncapsulation.None,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectComponent), multi: true }],
})
export class SelectComponent implements ControlValueAccessor, OnDestroy {

  options  = input<SelectOption[]>([]);
  multiple = input<boolean>(false);

  @Input() placeholder = 'Sélectionner…';
  @Input() disabled    = false;
  @Input() size:         SelectSize = 'medium';
  @Input() addIconLeft  = false;
  @Input() addIconRight = false;
  @Input() withCheckboxes = false;

  @Output() selectionChange = new EventEmitter<SelectOption>();
  @Output() selectionsChange = new EventEmitter<SelectOption[]>();

  private readonly _el = inject(ElementRef<HTMLElement>);

  readonly isOpen     = signal(false);
  readonly isFocused  = signal(false);
  readonly selectedId = signal<any>(null);
  readonly selectedIds = signal<any[]>([]);

  readonly selectedOption = computed(() => this.options().find(o => o.id === this.selectedId()) ?? null);
  readonly selectedOptions = computed(() => {
    const ids = this.selectedIds();
    return this.options().filter(o => ids.includes(o.id));
  });
  readonly selectedCount = computed(() => this.selectedOptions().length);
  readonly displayLabel   = computed(() => {
    if (this.multiple()) {
      const labels = this.selectedOptions().map(opt => opt.label);
      return labels.join(', ');
    }
    return this.selectedOption()?.label ?? '';
  });

  readonly dropdownStyle = signal<Record<string, string>>({});

  // ── Fermeture au clic extérieur ───────────────────────────────────────────
  private readonly _onDocClick = (e: MouseEvent) => {
    if (!this._el.nativeElement.contains(e.target as Node)) this.isOpen.set(false);
  };

  private readonly _onScroll = () => { if (this.isOpen()) this._updatePos(); };

  constructor() {
    afterNextRender(() => {
      document.addEventListener('click',  this._onDocClick, true);
      window.addEventListener('scroll',   this._onScroll,   true);
      window.addEventListener('resize',   this._onScroll);
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click',  this._onDocClick, true);
    window.removeEventListener('scroll',   this._onScroll,   true);
    window.removeEventListener('resize',   this._onScroll);
  }

  // ── CVA ───────────────────────────────────────────────────────────────────
  private _onChange:  (v: any) => void = () => {};
  private _onTouched: () => void       = () => {};

  writeValue(val: any): void {
    if (this.multiple()) {
      this.selectedIds.set(Array.isArray(val) ? val : []);
      return;
    }
    this.selectedId.set(val ?? null);
  }
  registerOnChange(fn: (v: any) => void): void { this._onChange = fn; }
  registerOnTouched(fn: () => void): void      { this._onTouched = fn; }
  setDisabledState(d: boolean): void           { this.disabled = d; }

  // ── Interactions ──────────────────────────────────────────────────────────
  toggle(): void {
    if (this.disabled) return;
    if (!this.isOpen()) { this._updatePos(); this.isOpen.set(true); }
    else this.isOpen.set(false);
  }

  private _updatePos(): void {
    const rect = this._el.nativeElement.getBoundingClientRect();

    // Un ancêtre transformé (ex. animation `transform` du popup) devient le
    // bloc conteneur des descendants `position: fixed`. On compense son offset
    // pour que les coordonnées viewport restent correctes.
    let offsetTop = 0;
    let offsetLeft = 0;
    let parent: HTMLElement | null = this._el.nativeElement.parentElement;
    while (parent && parent !== document.body) {
      const cs = getComputedStyle(parent);
      if (
        cs.transform !== 'none' ||
        cs.perspective !== 'none' ||
        cs.filter !== 'none' ||
        cs.willChange.includes('transform')
      ) {
        const pRect = parent.getBoundingClientRect();
        offsetTop = pRect.top;
        offsetLeft = pRect.left;
        break;
      }
      parent = parent.parentElement;
    }

    this.dropdownStyle.set({
      top:   `${rect.bottom + 4 - offsetTop}px`,
      left:  `${rect.left - offsetLeft}px`,
      width: `${rect.width}px`,
    });
  }

  selectOption(opt: SelectOption): void {
    if (opt.disabled) return;
    if (this.multiple()) {
      this.toggleMultipleOption(opt);
      return;
    }
    this.selectedId.set(opt.id);
    this._onChange(opt.id);
    this._onTouched();
    this.selectionChange.emit(opt);
    this.isOpen.set(false);
  }

  isSelected(opt: SelectOption): boolean { return this.selectedId() === opt.id; }
  isSelectedInMulti(opt: SelectOption): boolean { return this.selectedIds().includes(opt.id); }

  toggleMultipleOption(opt: SelectOption, nextChecked?: boolean): void {
    if (opt.disabled) return;
    const current = this.selectedIds();
    const exists = current.includes(opt.id);
    const shouldAdd = nextChecked !== undefined ? nextChecked : !exists;
    const next = shouldAdd
      ? (exists ? current : [...current, opt.id])
      : current.filter(id => id !== opt.id);

    this.selectedIds.set(next);
    this._onChange(next);
    this._onTouched();
    const selected = this.options().filter(o => next.includes(o.id));
    this.selectionsChange.emit(selected);
  }

  onFocus(): void { this.isFocused.set(true); }
  onBlur(): void  { this.isFocused.set(false); this._onTouched(); }
}